import * as Sentry from '@sentry/astro'
import {
  mapCartForStorefront,
  mapCartLineItemsForShopifyAdd,
  mapCartLineItemsForShopifyUpdate,
} from '../mappers/cart.mapper'
import {
  addLineItemsCartMutation,
  updateLineItemsCartMutation,
  createCartMutation,
  getCartQuery,
  updateAttributes,
  removeLineItemsCartMutation,
} from '../gql/cart.gql'
import { encode } from '../helpers/general.helpers'

const zarazGA4id = 'google-analytics_v4_f7e9__ga4sid='
const gaId = '_ga='
const getCookieById = (id) => {
  const parts = `; ${document.cookie}`.split(`; ${id}`)
  if (parts.length === 2) {
    return parts.pop().split(';').shift()
  }
}

const findLineItemAttrValue = (attributes = [], key) => {
  if (!attributes.length) return null

  const foundAttr = attributes.find((attr) => attr.key === key)

  if (!foundAttr) return null

  return foundAttr.value
}

const countSubAndNonSubLineItems = (items) => {
  return items.reduce(
    (acc, { node }) => {
      const hasBundleID = findLineItemAttrValue(node.attributes, '_bundle_id')

      if (node.sellingPlanAllocation || hasBundleID) {
        acc.sub++
      } else {
        acc.nonSub++
      }
      return acc
    },
    {
      sub: 0,
      nonSub: 0,
    },
  )
}

const buildCustomCartAttribute = (key, value) => {
  if (!CART_REPORTING_ORDER_ATTRIBUTE_VALUES.includes(value)) {
    console.warn(`Invalid prop value on cart attribute: ${key}`)
  }

  return {
    key,
    value,
  }
}

const CART_REPORTING_ORDER_ATTRIBUTE_VALUES = [
  'subscription',
  'non-subscription',
  'combo',
]

const getOrderLevelAttributes = (lineItems) => {
  if (!lineItems || !lineItems?.length) {
    return []
  }

  const attributes = []

  const { sub, nonSub } = countSubAndNonSubLineItems(lineItems)

  if (sub && !nonSub) {
    attributes.push(
      buildCustomCartAttribute('_order_purchase_type', 'subscription'),
    )
  }

  if (sub && nonSub) {
    attributes.push(buildCustomCartAttribute('_order_purchase_type', 'combo'))
  }

  if (!sub && nonSub) {
    attributes.push(
      buildCustomCartAttribute('_order_purchase_type', 'non-subscription'),
    )
  }

  return attributes
}

const addAnalyticsAttributesToCart = async ({ cart, query, cartId }) => {
  if (!cart) {
    return
  }

  const gaCookieValue = getCookieById(gaId)
  const zarazCookieValue = getCookieById(zarazGA4id)
  const splitValue = window.abBranch === 'production' ? 'default' : 'experiment'

  const {
    lines: { edges },
  } = cart

  const orderAttrs = getOrderLevelAttributes(edges)
  const attributes = [...orderAttrs]

  gaCookieValue && attributes.push({ key: 'gaId', value: gaCookieValue })

  zarazCookieValue &&
    attributes.push({ key: 'zarazGA4id', value: zarazCookieValue })

  splitValue && attributes.push({ key: 'splitTest', value: splitValue })

  attributes.push({ key: 'ab_version', value: 'RWLD001-3-V' })
  const variables = {
    attributes,
    cartId,
  }

  await query('cart.updateAttributes', updateAttributes, variables)
}

export const createCartMethods = ({
  query,
  options,
  cookies,
  setLocalCart,
  clearLocalCart,
  getCartID,
}) => ({
  fetchById: async (cartId) => {
    const cartResponse = await query('cart.fetchById', getCartQuery, { cartId })
    const cart = cartResponse?.cart

    if (cart) {
      await addAnalyticsAttributesToCart({ cart, query, cartId })
    }
    return cart ? mapCartForStorefront(cart) : null
  },
  createAndSetCart: async (cookieKey) => {
    const cartCookieKey = cookieKey || 'wild_cart_id'
    const createResponse = await query(
      'cart.createAndSetCart',
      createCartMutation,
    )
    const cart = createResponse?.cartCreate?.cart

    if (!cart) {
      console.error('Failed to create cart', createResponse)
      return
    }

    cookies.set(cartCookieKey, encode(cart.id), { expires: 1 })
    setLocalCart(mapCartForStorefront(cart))
  },
  fetchAndSetStoreCode: (storeCode) => {
    const cartStoreCodeCookieKey =
      options.cartStoreCodeCookieKey || 'wild_store_code'
    const existingCookieStoreCode = cookies.get(cartStoreCodeCookieKey)
    if (existingCookieStoreCode && existingCookieStoreCode === storeCode) {
      return storeCode
    }
    cookies.set(cartStoreCodeCookieKey, storeCode, { expires: 7 })
  },
  clear: async () => {
    const cartCookieKey = options.cartCookieKey || 'wild_cart_id'
    const cartStoreCodeCookieKey =
      options.cartStoreCodeCookieKey || 'wild_store_code'
    cookies.remove(cartCookieKey)
    cookies.remove(cartStoreCodeCookieKey)
    await clearLocalCart()
  },
  getUrl: (cart, { shopDomain, langCode }) => {
    try {
      if (!cart || !cart.webUrl) return null

      const cartUrlDelimiter = '/cart/c'

      if (!cart.webUrl.includes(cartUrlDelimiter)) return null

      const {
        pathname,
        search,
        searchParams: { size },
      } = new URL(cart.webUrl)

      const additionalSearch = [['locale', langCode]]
        .filter(([_key, value]) => !!value)
        .map(([key, value]) => `${key}=${value}`)
        .join('&')

      const searchConnector = size > 0 ? '&' : '?'

      return `https://${shopDomain}${pathname}${search}${
        additionalSearch ? `${searchConnector}${additionalSearch}` : ''
      }`
    } catch (error) {
      console.log(error)
      Sentry.captureException(error)
    }
  },
  lineItems: {
    add: async (lineItemsToAdd) => {
      const mappedLineItemsToAdd = mapCartLineItemsForShopifyAdd(lineItemsToAdd)

      if (!mappedLineItemsToAdd) return

      const cartId = getCartID()

      const variables = {
        cartId,
        lines: mappedLineItemsToAdd,
      }

      const {
        cartLinesAdd: { cart },
      } = await query('cart.lineItems.add', addLineItemsCartMutation, variables)
      if (cart) {
        await setLocalCart(mapCartForStorefront(cart))
        await addAnalyticsAttributesToCart({ cart, query, cartId })
        return cart
      }
    },
    update: async (lineItemsToUpdate) => {
      const mappedLineItemsToUpdate =
        mapCartLineItemsForShopifyUpdate(lineItemsToUpdate)

      if (!mappedLineItemsToUpdate) return

      const cartId = getCartID()

      const variables = {
        cartId,
        lines: mappedLineItemsToUpdate,
      }

      const {
        cartLinesUpdate: { cart },
      } = await query(
        'cart.lineItems.update',
        updateLineItemsCartMutation,
        variables,
      )
      if (cart) {
        await setLocalCart(mapCartForStorefront(cart))
        await addAnalyticsAttributesToCart({ cart, query, cartId })
        return cart
      }
    },
    remove: async (lineItemIdsToRemove) => {
      const cartId = getCartID()
      const variables = {
        cartId,
        lineIds: lineItemIdsToRemove,
      }

      const {
        cartLinesRemove: { cart },
      } = await query(
        'cart.lineItems.remove',
        removeLineItemsCartMutation,
        variables,
      )
      if (cart) {
        await setLocalCart(mapCartForStorefront(cart))
        await addAnalyticsAttributesToCart({ cart, query, cartId })
        return cart
      }
    },
  },
})
